import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';

import {
  useMailTemplatesListQuery,
  useMailGroupContactListQuery,
  useMailCandidatesListQuery,
  useMailJobProgressQuery,
  useMailJobsHistoryQuery,
  useMailSyncContactListMutation,
  useMailSendToGroupMutation,
  MailTemplatesListQuery as MailTemplatesListDoc,
  MailGroupContactListQuery as MailGroupContactListDoc,
  MailCandidatesListQuery as MailCandidatesListDoc,
  MailJobProgressQuery as MailJobProgressDoc,
  MailJobsHistoryQuery as MailJobsHistoryDoc,
  MailRecipientInput,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { useStableRefetch } from './useStableRefetch';

/* ---------- ТЗ 0015 — SendGrid email mailings (group card) ---------- */

/** A dynamic template pulled live from SendGrid. */
export type IMailTemplate = MailTemplatesListDoc['sendgridTemplates'][number];

/** The SendGrid Marketing list mirroring the group's students. */
export type IMailContactList = NonNullable<
  MailGroupContactListDoc['sendgridGroupList']
>;

/** One row of the recipient picker: a student + (optionally) their invoice. */
export type IMailCandidate = MailCandidatesListDoc['groupMailCandidates'][number];

/** A mailing run with live progress + per-recipient telemetry. */
export type IMailJob = NonNullable<MailJobProgressDoc['mailJob']>;

/** A history row (same aggregate shape, slimmer recipient selection). */
export type IMailJobSummary = MailJobsHistoryDoc['mailJobs'][number];

/** Per-recipient outcome inside a history row. */
export type IMailJobRecipient = IMailJobSummary['recipients'][number];

/**
 * Which students the picker offers. Mirrors the backend `mode` argument —
 * a raw lowercase string, NOT a GraphQL enum.
 */
export type MailCandidateMode = 'unpaid' | 'overdue' | 'all';

/**
 * Job/recipient statuses arrive as RAW lowercase strings (the backend
 * deliberately exposes `String`, not an enum — see the spec's contract note).
 * A job stops moving once it reaches one of these.
 */
/** Consecutive progress-poll failures tolerated before giving up on polling. */
export const POLL_ERROR_LIMIT = 4;

export const MAIL_JOB_TERMINAL: readonly string[] = [
  'success',
  'partial',
  'failed',
];

/* ---------- templates ---------- */

interface IMailTemplatesHook {
  templates: IMailTemplate[];
  loading: boolean;
  error?: ApolloError;
}

/**
 * The SendGrid dynamic-template catalogue. An empty list is a legitimate,
 * non-error outcome (no dynamic templates exist in the account yet) — the UI
 * distinguishes it from `loading` and says so explicitly.
 */
export const useMailTemplates = (skip = false): IMailTemplatesHook => {
  const { data, loading, error } = useMailTemplatesListQuery({
    skip,
    fetchPolicy: 'cache-and-network',
  });

  return {
    templates: data?.sendgridTemplates || [],
    loading,
    error,
  };
};

/* ---------- contact list + sync ---------- */

interface IMailContactListHook {
  list?: IMailContactList;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
  /**
   * `syncGroupSendgridList` — creates the list on first run, else refreshes
   * it. Closed contracts are never included: the list is upsert-only on
   * SendGrid's side, so widening it once could not be undone (задача ERP #67 —
   * the opt-in lives on the one-off send only).
   */
  sync: () => Promise<boolean>;
  syncing: boolean;
  /**
   * Human-readable failure from the LAST sync attempt, if any. The backend
   * already localizes the common case (an API key without the Marketing
   * scope), so it is rendered as-is — as a warning, never a crash.
   */
  syncError: string;
}

/**
 * The group's SendGrid contact list + the create/refresh action.
 *
 * `syncGroupSendgridList` reports trouble two ways: a `customErrors` entry
 * (validation/permission) and a persisted `list.errorMessage` (the last
 * import's failure, e.g. a missing Marketing scope on the API key). Both are
 * surfaced through `syncError` so the tab can render one warning strip.
 */
export const useGroupMailContactList = (
  groupId: string,
): IMailContactListHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [syncError, setSyncError] = useState('');

  const { data, loading, error, refetch } = useMailGroupContactListQuery({
    variables: { groupId },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const stableRefetch = useStableRefetch(refetch);

  const [runSync, { loading: syncing }] = useMailSyncContactListMutation();

  const sync = useCallback(async (): Promise<boolean> => {
    if (!groupId) return false;
    setSyncError('');
    try {
      const res = await runSync({ variables: { groupId } });
      const payload = res.data?.syncGroupSendgridList;
      if (!payload?.success) {
        const detail = payload?.customErrors?.[0]?.message || '';
        setSyncError(detail || t('pages.groupDetail.mailings.list.syncFailed'));
        addToast({
          type: GlobalToastTypeNames.GroupEdit,
          data: {
            type: ToastTypes.error,
            title: t('pages.groupDetail.mailings.list.syncFailed'),
            text: detail,
          },
        });
        return false;
      }
      // A "successful" sync can still carry the previous import's error text.
      setSyncError(payload.list?.errorMessage || '');
      addToast({
        type: GlobalToastTypeNames.GroupEdit,
        data: {
          type: ToastTypes.success,
          title: t('pages.groupDetail.mailings.list.syncDone'),
        },
      });
      stableRefetch();
      return true;
    } catch (e) {
      setSyncError((e as Error).message);
      addToast({
        type: GlobalToastTypeNames.GroupEdit,
        data: {
          type: ToastTypes.error,
          title: t('pages.groupDetail.mailings.list.syncFailed'),
          text: (e as Error).message,
        },
      });
      return false;
    }
  }, [groupId, runSync, addToast, stableRefetch, t]);

  const list = data?.sendgridGroupList ?? undefined;

  return {
    list,
    loading,
    error,
    refetch: stableRefetch,
    sync,
    syncing,
    // The stored error survives a reload; a fresh attempt overwrites it.
    syncError: syncError || list?.errorMessage || '',
  };
};

/* ---------- recipient candidates ---------- */

interface IMailCandidatesHook {
  candidates: IMailCandidate[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

const NO_CANDIDATES: IMailCandidate[] = [];

/**
 * The recipient picker's rows for one filter `mode`.
 *
 * `includeTerminated` (задача ERP #67) adds students whose every agreement in
 * the group is closed; those rows carry `agreementTerminated`. Flipping it
 * changes the query variables, and with no cache entry yet Apollo hands back
 * `data: undefined` while loading — the picker would fall to its «Загрузка…»
 * placeholder and the table (with the operator's ticks) would unmount. So for
 * the SAME group+mode the last delivered rows are kept on screen until the new
 * ones land. A different mode or group never inherits stale rows.
 *
 * `cache-and-network` means a background refetch can hand the consumer a NEW
 * array identity mid-compose. Consumers must therefore never key a
 * selection-reset effect on this array — see `MailComposer`, which reads it
 * through a ref and resets only when `mode` changes.
 */
export const useGroupMailCandidates = (
  groupId: string,
  mode: MailCandidateMode,
  includeTerminated = false,
): IMailCandidatesHook => {
  const { data, loading, error, refetch } = useMailCandidatesListQuery({
    variables: { groupId, mode, includeTerminated },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const stableRefetch = useStableRefetch(refetch);

  const key = `${groupId}|${mode}`;
  const lastRows = useRef<{ key: string; rows: IMailCandidate[] } | null>(null);
  const rows = data?.groupMailCandidates;
  if (rows) lastRows.current = { key, rows };
  const kept = lastRows.current?.key === key ? lastRows.current.rows : null;

  return {
    candidates: rows || kept || NO_CANDIDATES,
    loading,
    error,
    refetch: stableRefetch,
  };
};

/* ---------- send + live progress ---------- */

interface IGroupMailSendHook {
  /** Fire the mailing. Resolves `true` when the job was accepted. */
  send: (input: {
    templateId: string;
    templateName?: string;
    recipients: MailRecipientInput[];
  }) => Promise<boolean>;
  /** The mutation is in flight. */
  sending: boolean;
  /** The accepted job's id — non-null from acceptance until `reset()`. */
  jobId: string | null;
  /** Live progress for `jobId` (polled until terminal). */
  job?: IMailJob;
  /** The job reached a terminal status; polling has stopped. */
  done: boolean;
  /**
   * Progress polling gave up after repeated failures. The job itself is almost
   * certainly still fine on the server — we just stopped being able to watch
   * it, so the UI must say so and offer a way out instead of staying locked.
   */
  pollFailed: boolean;
  /** Clear job + done so a new mailing can be composed. */
  reset: () => void;
}

/**
 * Owns one compose→send→watch cycle.
 *
 * Two lifecycle rules this hook exists to enforce:
 *
 *  1. `done` is SEPARATE state, never derived inline into `pollInterval`.
 *     Reading `job.status` straight into the interval keeps one extra poll
 *     running past the terminal render.
 *  2. Sending is ONE-SHOT per compose. `jobId` gates it in state, and a ref
 *     mirror gates it synchronously — two fast clicks resolve before either
 *     `jobId` or the mutation's `loading` has committed, and a second accepted
 *     job means every recipient is mailed twice.
 */
export const useGroupMailSend = (
  groupId: string,
  onFinished?: () => void,
): IGroupMailSendHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const [jobId, setJobId] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Synchronous mirror of `jobId` + the in-flight flag: state updates are
  // async, so only a ref can reject a second click within the same tick.
  const sendGuard = useRef(false);
  // Fire `onFinished` exactly once per job, without listing it as a dep of a
  // job-keyed effect (the callback identity may change every render).
  const finishedFor = useRef<string | null>(null);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const [runSend, { loading: sending }] = useMailSendToGroupMutation();

  // A persistently failing poll must not leave the composer locked and silent.
  // `done` only flips on a SUCCESSFUL response carrying a terminal status, so
  // without this an expired token or a redeploy mid-send would disable every
  // control forever with no message and no way out.
  const [pollFailed, setPollFailed] = useState(false);
  const pollErrors = useRef(0);

  const { data: jobData, error: pollError } = useMailJobProgressQuery({
    variables: { id: jobId || '' },
    skip: !jobId,
    fetchPolicy: 'network-only',
    pollInterval: done || pollFailed ? 0 : 1500,
  });
  const job = jobData?.mailJob ?? undefined;

  useEffect(() => {
    if (!pollError) {
      pollErrors.current = 0;
      return;
    }
    pollErrors.current += 1;
    // Tolerate a blip; give up after a few consecutive failures so polling
    // stops and the UI can offer an escape.
    if (pollErrors.current >= POLL_ERROR_LIMIT) setPollFailed(true);
  }, [pollError]);

  useEffect(() => {
    if (!job || !MAIL_JOB_TERMINAL.includes(job.status || '')) return;
    setDone(true);
    if (finishedFor.current !== job.id) {
      finishedFor.current = job.id;
      onFinishedRef.current?.();
    }
  }, [job]);

  const send = useCallback(
    async (input: {
      templateId: string;
      templateName?: string;
      recipients: MailRecipientInput[];
    }): Promise<boolean> => {
      if (!groupId || sendGuard.current) return false;
      sendGuard.current = true;
      try {
        const res = await runSend({
          variables: {
            groupId,
            templateId: input.templateId,
            templateName: input.templateName,
            recipients: input.recipients,
          },
        });
        const payload = res.data?.sendGroupTemplateEmail;
        if (!payload?.success) {
          const first = payload?.customErrors?.[0];
          // Перепроверка 0084: у отказа «сократитель не настроен» свой
          // текст на языке интерфейса — ответ сервера только по-русски.
          const text =
            first?.code === 'SHORTLINK_DISABLED'
              ? t('pages.groupDetail.mailings.send.shortlinkDisabled')
              : first?.message || '';
          addToast({
            type: GlobalToastTypeNames.GroupEdit,
            data: {
              type: ToastTypes.error,
              title: t('pages.groupDetail.mailings.send.failed'),
              text,
            },
          });
          // Nothing was accepted — release the guard so the operator can retry.
          sendGuard.current = false;
          return false;
        }
        setJobId(payload.mailJobId || null);
        setDone(false);
        finishedFor.current = null;
        // Guard intentionally STAYS closed: a job exists, so this compose is
        // spent. `reset()` is the only way back to a sendable state.
        return true;
      } catch (e) {
        addToast({
          type: GlobalToastTypeNames.GroupEdit,
          data: {
            type: ToastTypes.error,
            title: t('pages.groupDetail.mailings.send.failed'),
            text: (e as Error).message,
          },
        });
        sendGuard.current = false;
        return false;
      }
    },
    [groupId, runSend, addToast, t],
  );

  const reset = useCallback(() => {
    sendGuard.current = false;
    finishedFor.current = null;
    pollErrors.current = 0;
    setJobId(null);
    setDone(false);
    setPollFailed(false);
  }, []);

  return { send, sending, jobId, job, done, pollFailed, reset };
};

/* ---------- history ---------- */

interface IMailJobsHistoryHook {
  jobs: IMailJobSummary[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/** Past mailings for the group, newest first (backend-ordered). */
export const useGroupMailJobs = (
  groupId: string,
  limit = 20,
): IMailJobsHistoryHook => {
  const { data, loading, error, refetch } = useMailJobsHistoryQuery({
    variables: { groupId, limit },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const stableRefetch = useStableRefetch(refetch);

  return {
    jobs: data?.mailJobs || [],
    loading,
    error,
    refetch: stableRefetch,
  };
};
